import React from "react";
import { Card, Col, Progress, Row, Tooltip } from "antd";
import {
  DeviationTab,
  fmtINR,
  fmtINRCompact,
  fmtINRSigned,
  num,
} from "./deviationShared";

// KPI row for the active tab. The COVERAGE tile is the important one: the
// impact totals only sum the rows whose rate could actually be measured, so
// without it a small impact number is indistinguishable from a large one that
// simply had nothing to measure.

const INK = "#022c57";
const MUTED = "#8094ae";
const OVER = "#cf1322";
const UNDER = "#237804";

const Tile: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: string;
  color?: string;
  extra?: React.ReactNode;
}> = ({ label, value, hint, color, extra }) => (
  <Col xs={24} sm={12} lg={6}>
    <Card size="small" style={{ borderColor: "#e4e9f0", height: "100%" }}>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: MUTED }}>
        {hint ? (
          <Tooltip title={hint}>
            <span style={{ borderBottom: "1px dotted #c9d2e3" }}>{label}</span>
          </Tooltip>
        ) : (
          label
        )}
      </div>
      <div
        style={{
          fontSize: 21,
          fontWeight: 700,
          color: color || INK,
          fontVariantNumeric: "tabular-nums",
          marginTop: 4,
          lineHeight: 1.2,
        }}
      >
        {value}
      </div>
      {extra}
    </Card>
  </Col>
);

interface Props {
  tab?: DeviationTab;
  showNormDelta: boolean;
}

const DeviationKpiCards: React.FC<Props> = ({ tab, showNormDelta }) => {
  const plannedCost = num(tab?.plannedCost);
  const measuredCost = num(tab?.measuredPlannedCost);
  const rowCount = tab?.rowCount || 0;
  const measuredRowCount = tab?.measuredRowCount || 0;
  const coveragePct = plannedCost > 0 ? (measuredCost / plannedCost) * 100 : 0;

  const impact = num(tab?.impactValue);
  const normImpact = num(tab?.targetImpactValue);
  const hasImpact = measuredRowCount > 0;
  const hasNormImpact = (tab?.targetedRowCount || 0) > 0;

  return (
    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
      <Tile
        label="Planned Cost"
        hint="Total allocated 3M cost for this tab under the current filters"
        value={
          <Tooltip title={fmtINR(plannedCost)}>
            <span>{fmtINRCompact(plannedCost)}</span>
          </Tooltip>
        }
        extra={
          <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>
            {rowCount} item{rowCount === 1 ? "" : "s"}
          </div>
        }
      />
      <Tile
        label="Impact / Variance"
        hint="Σ (Actual Rate − BOQ Rate) × allocated qty, over the rows where an actual rate exists"
        color={hasImpact ? (impact > 0 ? OVER : impact < 0 ? UNDER : INK) : MUTED}
        value={
          hasImpact ? (
            <Tooltip title={fmtINRSigned(impact, true)}>
              <span>{fmtINRSigned(impact)}</span>
            </Tooltip>
          ) : (
            <span style={{ fontSize: 15, fontWeight: 600 }}>Not measurable</span>
          )
        }
        extra={
          <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>
            {hasImpact
              ? `from ${measuredRowCount} measured item${measuredRowCount === 1 ? "" : "s"}`
              : "no actual rates captured yet"}
          </div>
        }
      />
      <Tile
        label="Actual-rate Coverage"
        hint="How much of the planned cost has an actual rate behind it. The impact figure only speaks for this slice — the rest is unmeasured, not zero."
        value={`${coveragePct.toFixed(1)}%`}
        color={coveragePct > 0 ? INK : MUTED}
        extra={
          <>
            <Progress
              percent={Math.min(100, coveragePct)}
              showInfo={false}
              strokeColor="#2557d6"
              size="small"
              style={{ marginTop: 4, marginBottom: 0 }}
            />
            <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>
              {measuredRowCount}/{rowCount} items ·{" "}
              <Tooltip title={fmtINR(measuredCost)}>
                <span>{fmtINRCompact(measuredCost)}</span>
              </Tooltip>{" "}
              of{" "}
              <Tooltip title={fmtINR(plannedCost)}>
                <span>{fmtINRCompact(plannedCost)}</span>
              </Tooltip>
            </div>
          </>
        }
      />
      {showNormDelta ? (
        <Tile
          label="Norm Re-pricing Impact"
          hint="Σ (Target Rate − BOQ Rate) × allocated qty — how much the current norm master has drifted from the rates this project was planned at"
          color={
            hasNormImpact
              ? normImpact > 0
                ? OVER
                : normImpact < 0
                  ? UNDER
                  : INK
              : MUTED
          }
          value={
            hasNormImpact ? (
              <Tooltip title={fmtINRSigned(normImpact, true)}>
                <span>{fmtINRSigned(normImpact)}</span>
              </Tooltip>
            ) : (
              <span style={{ fontSize: 15, fontWeight: 600 }}>Not measurable</span>
            )
          }
          extra={
            <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>
              {hasNormImpact
                ? `from ${tab?.targetedRowCount} norm-backed item${tab?.targetedRowCount === 1 ? "" : "s"}`
                : "no norm-master rates for this tab"}
            </div>
          }
        />
      ) : null}
    </Row>
  );
};

export default DeviationKpiCards;
